import { useEffect, useMemo, useRef, useState } from "react";

import type { QuestionCoverageResult } from "@digital-twins/shared-types";

import { AnalyticsEvents, track } from "@/lib/services/analytics";

import { assessQuestionnaireCoverageApi } from "../api/questionnaireDraftsApi";
import type { CoverageEntry } from "../components/TopicCoverageChip";

const isFlagged = (entry: QuestionCoverageResult | undefined): boolean =>
  entry?.level === "partial" || entry?.level === "out_of_domain";

interface UseQuestionnaireCoverageArgs {
  draftId: string | null | undefined;
  /** The accepted revision the builder shows; undefined until hydrated. */
  revision: number | undefined;
  /** The accepted revision's question ids, which show a spinner until
   * their first verdict arrives. */
  questionIds: ReadonlyArray<string>;
}

/**
 * Coverage of the accepted revision, scored and cached by the server (C20-1).
 *
 * One request per accepted revision: after a Save or an agent turn, not while
 * typing. The server reads the revision's questions, assets and audience
 * itself and reuses its stored verdict for any question whose wording and
 * audience are unchanged, so a reload makes no model call. A question keeps
 * its last verdict until the new one arrives; one without a verdict yet shows
 * a spinner.
 */
export function useQuestionnaireCoverage({
  draftId,
  revision,
  questionIds,
}: UseQuestionnaireCoverageArgs): Map<string, CoverageEntry> {
  const [results, setResults] = useState<Map<string, QuestionCoverageResult>>(new Map());
  const [pending, setPending] = useState(false);
  const resultsRef = useRef(results);
  resultsRef.current = results;

  useEffect(() => {
    setResults(new Map());
  }, [draftId]);

  useEffect(() => {
    if (!draftId || revision === undefined) return;
    const controller = new AbortController();
    setPending(true);
    assessQuestionnaireCoverageApi(draftId, revision, controller.signal)
      .then((assessment) => {
        if (controller.signal.aborted) return;
        const previous = resultsRef.current;
        const next = new Map(assessment.results.map((result) => [result.questionId, result]));
        for (const result of next.values()) {
          const before = previous.get(result.questionId);
          if (before && isFlagged(before) && !isFlagged(result)) {
            track(AnalyticsEvents.COVERAGE_FLAGGED_QUESTION_RESOLVED, {
              question_id: result.questionId,
              prev_level: before.level,
              new_level: result.level,
            });
          }
        }
        track(AnalyticsEvents.COVERAGE_ASSESSMENT_COMPUTED, {
          draft_id: draftId,
          question_count: next.size,
          flagged_count: [...next.values()].filter(isFlagged).length,
        });
        setResults(next);
      })
      // A failed or superseded request leaves the last verdicts in place; the
      // next accepted revision asks again.
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setPending(false);
      });
    return () => controller.abort();
  }, [draftId, revision]);

  const questionIdsKey = questionIds.join("\u0000");
  return useMemo(() => {
    const entries = new Map<string, CoverageEntry>(results);
    if (pending) {
      for (const id of questionIdsKey ? questionIdsKey.split("\u0000") : []) {
        if (!entries.has(id)) entries.set(id, "computing");
      }
    }
    return entries;
  }, [results, pending, questionIdsKey]);
}
